import { useEffect, useState } from 'react';
import queryString from 'query-string';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '../base-types';
import {
  Filters,
  SchemaFilterType,
  SchemaProps,
  Sort,
  SortDir,
} from '../components/table/types';
import { ResponseError } from '../resolver/types';

let timerId: NodeJS.Timeout;
let inProgress: boolean;
let request: any;

interface QueryDataProps {
  take: string;
  filters?: Filters;
  sortProp?: string;
  sortDir?: string;
}

const buildQueryData = ({
  filters,
  sort,
  setFiltersValues,
  setSortValues,
  take,
  schema,
  withoutTimeZone,
}: {
  filters?: Filters;
  sort?: Sort;
  setFiltersValues?: (data: Filters) => void;
  setSortValues?: (data: Sort) => void;
  schema: SchemaProps;
  take?: string;
  withoutTimeZone?: boolean;
}): QueryDataProps => {
  let queryData: QueryDataProps = {
    take: '20',
  };

  if (filters && Object.keys(filters).length) {
    if (setFiltersValues) {
      setFiltersValues(filters);
    }

    const filtersWithoutEmpty: Record<string, any> = {};

    Object.keys(filters).forEach((item) => {
      if (filters[item]) {
        if (filters[item] && item !== 'cursor') {
          filtersWithoutEmpty[item] = filters[item].value;
        }

        if (item === 'cursor' && filters[item]) {
          filtersWithoutEmpty.cursor = filters[item].value;
        }

        if (filters[item].type === SchemaFilterType.DATE_RANGE) {
          const { queryNames } = schema[item];

          if (filters[item].value?.length > 0) {
            const [from, to] = filters[item].value;

            if (from) {
              if (!withoutTimeZone) {
                const dateFrom = new Date(from);

                dateFrom.setHours(0);
                dateFrom.setMinutes(0);
                dateFrom.setSeconds(0);
                dateFrom.setMilliseconds(0);

                filtersWithoutEmpty[(queryNames as string[])[0]] =
                  dateFrom.toISOString();
              } else {
                const dateFrom = new Date(from);

                filtersWithoutEmpty[(queryNames as string[])[0]] =
                  dateFrom.toISOString();
              }
            }

            if (filters[item].value.length > 1) {
              if (!withoutTimeZone) {
                const dateTo = new Date(to);

                dateTo.setHours(23);
                dateTo.setMinutes(59);
                dateTo.setSeconds(59);
                dateTo.setMilliseconds(99);

                filtersWithoutEmpty[(queryNames as string[])[1]] =
                  dateTo.toISOString();
              } else {
                const dateTo = new Date(to);
                filtersWithoutEmpty[(queryNames as string[])[1]] =
                  dateTo.toISOString();
              }
            }

            delete filtersWithoutEmpty[item];
          }
        }

        if (filters[item].type === SchemaFilterType.DATE) {
          if (filters[item].value?.length > 0) {
            const [from] = filters[item].value;

            if (from) {
              filtersWithoutEmpty[item] = new Date(from).toISOString();
            }
          }
        }
      }
    });

    queryData = {
      ...queryData,
      ...filtersWithoutEmpty,
    };
  }

  if (sort?.sortProp && sort?.sortDir) {
    if (setSortValues) {
      setSortValues(sort);
    }

    queryData = {
      ...queryData,
      sortProp: sort.sortProp,
      sortDir: sort.sortDir,
    };
  }

  if (take) {
    queryData.take = take;
  }

  return queryData;
};

export default function useGetList<T>({
  getList,
  onFilterChange,
  onSortChange,
  itemId,
  schema,
  isRetry,
  delay = 5000,
  sortProp,
  entityId,
  withoutChangeSearchParams,
}: {
  getList: any;
  onFilterChange: (data: Filters) => void;
  onSortChange: (
    name: string,
    diretion?: string,
    queryFilters?: Filters,
  ) => void;
  schema: SchemaProps;
  itemId?: string;
  entityId?: string;
  isRetry?: boolean;
  delay?: number;
  sortProp?: string;
  withoutChangeSearchParams?: boolean;
}) {
  const [filtersValues, setFiltersValues] = useState<Filters>({});
  const [sortValues, setSortValues] = useState<Sort>();
  const [errorList, setErrorList] = useState('');
  const [searchParams] = useSearchParams();
  const [queryValue, setQueryValue] = useState<any>();
  const [isFirstRequest, setIsFirstRequest] = useState(false);

  inProgress = true;

  const getData = async (query: QueryDataProps, filters?: Filters) => {
    setErrorList('');

    let getListOptions;
    const getListQuery = `?${queryString.stringify(query)}`;

    if (itemId) {
      getListOptions = {
        id: itemId,
        query: getListQuery,
      };

      if (entityId) {
        getListOptions = {
          id: itemId,
          query: getListQuery,
          entityId,
        };
      }
    } else {
      getListOptions = getListQuery;
    }

    request = getList(getListOptions);

    const response = await request.unwrap();

    const { data, error } = (await request.unwrap()) as {
      data: {
        pageInfo: Pagination;
        edges: T[];
      };
      error: ResponseError;
    };

    if (error?.data?.error) {
      if (typeof error?.data?.error === 'string' && !error?.data?.message) {
        setErrorList(error?.data?.error);
      } else if (
        error?.data?.message &&
        typeof error?.data?.message === 'string'
      ) {
        setErrorList(error?.data?.message);
      } else if (error?.data?.message && Array.isArray(error?.data?.message)) {
        const messages: string[] = [];

        error?.data?.message?.forEach((item) => {
          messages.push(item.value as string);
        });

        setErrorList(messages?.join(', '));
      } else {
        setErrorList('Internal Server Error. Try again.');
      }

      inProgress = false;
    } else if (error?.status === 500) {
      setErrorList('Internal Server Error. Try again.');
    } else if (response?.statusCode === 500) {
      setErrorList('Internal Server Error. Try again.');
    } else if (data.edges) {
      inProgress = false;
    }

    setQueryValue(query);

    if (
      data.pageInfo &&
      !data.pageInfo.hasPreviousPage &&
      filters?.cursor &&
      onFilterChange
    ) {
      onFilterChange({
        ...filters,
        cursor: {
          value: [],
        },
      });
    }
  };

  useEffect(() => {
    if (
      queryValue?.sortProp &&
      schema?.sortProp?.value?.default &&
      isFirstRequest
    ) {
      onSortChange(
        queryValue?.sortProp as string,
        queryValue.sortDir,
        filtersValues,
      );

      setIsFirstRequest(false);
    }

    if (
      queryValue?.sortProp &&
      queryValue?.sortProp !== sortValues?.sortProp &&
      schema?.sortProp?.value?.default
    ) {
      onSortChange(
        queryValue?.sortProp as string,
        queryValue.sortDir,
        filtersValues,
      );
    }
  }, [queryValue, schema, filtersValues]);

  const onClearTimer = async (leave?: boolean) => {
    inProgress = false;

    if (leave) {
      request?.abort();
    }

    await clearTimeout(timerId);
  };

  const onClearError = () => {
    setErrorList('');
  };

  const shedule = async (queryData: QueryDataProps, isFirst?: boolean) => {
    await onClearTimer();

    const nextQueryData = { ...queryData };

    if (!nextQueryData.sortProp && sortProp) {
      nextQueryData.sortProp = sortProp;
      nextQueryData.sortDir = SortDir.DESC;
    }

    if (!inProgress) {
      if (isFirst) {
        await getData(nextQueryData, {});
      }

      timerId = setTimeout(async () => {
        await getData(nextQueryData, {});
        await shedule(nextQueryData);
      }, delay);
    }
  };

  const getListAction = async ({
    filters,
    sort,
    withoutTimeZone,
  }: {
    filters?: Filters;
    sort?: Sort;
    withoutTimeZone?: boolean;
  }) => {
    await onClearTimer();

    const queryData = buildQueryData({
      filters,
      sort,
      setFiltersValues,
      setSortValues,
      schema,
      withoutTimeZone,
    });

    if (!isFirstRequest) {
      setIsFirstRequest(true);
    }

    if (isRetry) {
      await shedule(queryData, true);
    } else {
      await getData(queryData, filters);
    }
  };

  const onFilter = async (values: Filters, withoutTimeZone?: boolean) => {
    await onClearTimer();
    const nextFilters = { ...values };

    if (nextFilters.cursor) {
      delete nextFilters.cursor;
    }

    onFilterChange(nextFilters);

    const queryData = buildQueryData({
      filters: nextFilters,
      setFiltersValues,
      sort: {
        sortProp: sortValues?.sortProp
          ? sortValues?.sortProp
          : nextFilters?.sortProp?.value && nextFilters?.sortProp?.value[0],
        sortDir: sortValues?.sortDir
          ? sortValues?.sortDir
          : nextFilters?.sortDir?.value[0],
      },
      schema,
      withoutTimeZone,
    });

    if (isRetry) {
      await shedule(queryData, true);
    } else {
      await getData(queryData, nextFilters);
    }
  };

  const onSort = async (name: string, sortDirValue?: SortDir) => {
    await onClearTimer();

    const nextFilters = { ...filtersValues };

    if (nextFilters.cursor) {
      delete nextFilters.cursor;
    }

    onFilterChange(nextFilters);

    const sortPropValue = searchParams.get('sortProp');
    let sortDir = sortDirValue || searchParams.get('sortDir');

    if (sortValues?.sortDir) {
      sortDir = sortDirValue || sortValues?.sortDir;
    }

    onSortChange(name, sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);

    let nextState: Sort = {
      sortProp: sortPropValue as string,
      sortDir: sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC,
    };

    if (sortPropValue === name) {
      nextState.sortDir = sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC;
    } else {
      nextState = {
        sortProp: name,
        // eslint-disable-next-line no-nested-ternary
        sortDir: withoutChangeSearchParams
          ? sortDir === SortDir.ASC
            ? SortDir.DESC
            : SortDir.ASC
          : SortDir.ASC,
      };
    }

    const queryData = buildQueryData({
      filters: nextFilters,
      sort: nextState,
      setSortValues,
      schema,
    });

    if (isRetry) {
      await shedule(queryData, true);
    } else {
      await getData(queryData);
    }
  };

  const setPage = async ({
    id,
    take,
    filters,
    withSkip,
  }: {
    id: string;
    take: string;
    filters: Filters;
    withSkip?: boolean;
  }) => {
    await onClearTimer();
    const nextFilters = { ...filters, cursor: { value: [id] } };

    const queryData = buildQueryData({
      filters: nextFilters,
      sort: sortValues,
      take: withSkip ? Math.abs(Number(take))?.toString() : take,
      schema,
    });

    onFilterChange(nextFilters);

    if (isRetry) {
      await shedule(queryData, true);
    } else {
      await getData(queryData, nextFilters);
    }
  };

  return {
    getListAction,
    setPage,
    onFilter,
    onSort,
    errorList,
    onClearError,
    onClearTimer,
  };
}
